import React from 'react';
import { Link } from 'react-router-dom';
import {
    BookOpen,
    Target,
    Briefcase,
    Award,
    GraduationCap,
    Hospital,
    FlaskConical,
    ChevronRight
} from 'lucide-react';
import Footer from '../../../components/Footer';
import heroimg from '../../../assets/schools/pharma/M pharma.jpg';
import HowToApplySection from '../../../components/HowToApplySection';

const MasterPharmacyPharmaceutics = () => {
    const programSections = [
        {
            title: "Program Outcomes",
            items: [
                "Equip students with in-depth theoretical and practical knowledge in formulation science, drug delivery systems, biopharmaceutics, and pharmaceutical technology.",
                "Foster the ability to design, conduct, and analyze independent research in the field of pharmaceutics, promoting innovation in dosage form design and drug delivery.",
                "Develop skills necessary for formulation development, manufacturing, quality control, and regulatory compliance, aligning with industry expectations.",
                "Encourage critical analysis and problem-solving capabilities to address real-world challenges in drug formulation and development.",
                "Promote adherence to ethical standards, good laboratory practices (GLP), and regulatory guidelines, ensuring responsible professional conduct.",
                "Prepare students for interdisciplinary collaboration and instill the habit of lifelong learning to adapt to advancements in pharmaceutical sciences.",
                "Build strong communication skills for scientific writing, presentations, and team collaboration, as well as leadership potential in research and industrial settings."
            ],
            icon: <Target className="text-emerald-700" size={40} />
        },
        {
            title: "Career Prospects",
            items: [
                "Pharmaceutical Industry (formulation R&D, production, quality assurance)",
                "Regulatory Affairs",
                "Academia and Research",
                "Clinical Research Organizations (CROs)",
                "Further study (Ph.D. in Pharmaceutics or related areas)"
            ],
            icon: <Briefcase className="text-emerald-700" size={40} />
        }
    ];

    const uniqueSellingPoints = [
        "Deep Focus on Drug Formulation & Delivery",
        "Strong Industry Orientation",
        "Balanced Curriculum",
        "Research-Driven Program",
        "Interdisciplinary Learning",
        "Renowned Domain Specific & Highly Rich Experienced Faculty",
        "Advance Diagnostic & Mentoring for Career and Placement throughout the Course",
        "Dynamic Programming & Core Skill Guidance throughout the course",
        "Start-up & Idea Innovation Lab for Career & Professional Growth"
    ];

    return (
        <div className="bg-emerald-50">
            <div className="bg-white text-emerald-900 font-sans mx-4 md:mx-8 lg:mx-16 my-4 mt-[70px]">
                {/* Hero Section */}
                <div className="relative bg-gradient-to-r from-emerald-50 to-white">
                    <div className="container mx-auto px-4 py-16 md:py-24 flex flex-col-reverse md:flex-row items-center">
                        <div className="md:w-1/2 space-y-6">
                            <h1 className="text-4xl md:text-5xl font-bold text-emerald-800 leading-tight">
                                Master in Pharmacy (M.Pharm – Pharmaceutics)
                                <span className="block text-lg mt-2 font-medium text-emerald-600">
                                    Maya Devi University, Dehradun
                                </span>
                            </h1>
                            <p className="text-emerald-700 text-lg leading-relaxed">
                                The M.Pharm in Pharmaceutics is a postgraduate program focused on the design, development, formulation, and evaluation of pharmaceutical dosage forms. The Department of Pharmaceutics at Maya Devi University transforms new chemical entities into safe and effective medications through innovative dosage forms like tablets, capsules, injections, and advanced drug delivery systems, ensuring stability, bioavailability, and therapeutic effectiveness.
                            </p>
                            <div className="flex space-x-4">
                                <Link
                                    to="https://admission.maya.edu.in/"
                                    className="bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-3 rounded-full 
                           transition duration-300 flex items-center group"
                                >
                                    Apply Now
                                    <ChevronRight
                                        className="ml-2 group-hover:translate-x-1 transition"
                                        size={20}
                                    />
                                </Link>
                            </div>
                        </div>
                        <div className="md:w-1/2 mb-8 md:mb-0 m-10 w-250 h-250">
                            <img
                                src={heroimg}
                                alt="Pharmaceutics Program"
                                className="rounded-xl shadow-lg hover:scale-105 transition duration-300 w-250 h-250"
                            />
                        </div>
                    </div>
                </div>

                {/* Eligibility and Program Details */}
                <div className="container mx-auto px-4 py-16 bg-emerald-50">
                    <div className="grid md:grid-cols-2 gap-8">
                        <div className="bg-white p-6 rounded-xl shadow-md">
                            <h2 className="text-2xl font-bold text-emerald-800 mb-4">
                                <GraduationCap className="inline mr-3 text-emerald-600" size={30} />
                                Eligibility Criteria
                            </h2>
                            <ul className="space-y-3 text-emerald-700">
                                <li>• B.Pharm with minimum 55% marks for General category</li>
                                <li>• Minimum 45% marks for SC/ST category</li>
                            </ul>
                        </div>
                        <div className="bg-white p-6 rounded-xl shadow-md">
                            <h2 className="text-2xl font-bold text-emerald-800 mb-4">
                                <Hospital className="inline mr-3 text-emerald-600" size={30} />
                                Program Details
                            </h2>
                            <table className="w-full text-emerald-700">
                                <tbody>
                                    <tr>
                                        <td className="font-semibold">Duration:</td>
                                        <td>2 Years</td>
                                    </tr>
                                    <tr>
                                        <td className="font-semibold">Approved By:</td>
                                        <td>Pharmacy Council of India (PCI)</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                {/* Program Sections */}
                <div className="container mx-auto px-4 py-16">
                    <div className="grid md:grid-cols-2 gap-8">
                        {programSections.map((section, index) => (
                            <div
                                key={index}
                                className="bg-emerald-50 p-6 rounded-xl shadow-md hover:shadow-lg transition"
                            >
                                <div className="flex items-center mb-4">
                                    {section.icon}
                                    <h3 className="text-2xl font-bold ml-4 text-emerald-800">
                                        {section.title}
                                    </h3>
                                </div>
                                <ul className="space-y-2 text-emerald-700">
                                    {section.items.map((item, idx) => (
                                        <li key={idx} className="flex items-start">
                                            <span className="mr-2 text-emerald-600">•</span>
                                            {item}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>
                </div>

                {/* USPs Section */}
                <div className="container mx-auto px-4 py-16">
                    <h2 className="text-3xl font-bold text-center text-emerald-800 mb-12">
                        Why Choose Us
                    </h2>
                    <div className="max-w-3xl mx-auto">
                        <div className="bg-emerald-50 p-8 rounded-xl shadow-lg hover:shadow-xl transition">
                            <div className="text-emerald-600 text-4xl mb-6 flex justify-center">
                                <Award size={60} />
                            </div>
                            <h3 className="text-2xl font-bold text-emerald-800 text-center mb-6">
                                Our USPs
                            </h3>
                            <ul className="space-y-3">
                                {uniqueSellingPoints.map((usp, index) => (
                                    <li
                                        key={index}
                                        className="flex items-center text-emerald-700 text-lg"
                                    >
                                        <div className="mr-3 text-emerald-600">•</div>
                                        {usp}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
            {/* How to Apply Section */}
            <HowToApplySection/>
            <div className="bg-emerald-50">
                <Footer />
            </div>
        </div>
    );
};

export default MasterPharmacyPharmaceutics;